import React, { FC } from "react";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  useTheme
} from "@mui/material";
import DateRangeSelector from "./DateRangeSelector";
import useMediaQuery from "@mui/material/useMediaQuery";
import { MetricTimeRange } from "./types/dashboardTypes";
import { useKivunovaTranslation } from "@kivunova/kivufrontendcommon";

export interface DashboardStoreFilterOption {
  id: string;
  name: string;
}

interface DashboardHeaderProps {
  /** Fired when the date range changes */
  onRangeChange: (range: MetricTimeRange) => void;
  /** Applied range from global state so the date control survives remounts during refetch. */
  metricRange?: MetricTimeRange;
  /** Company admin: store scope for dashboard analytics (null = all stores). */
  storeFilter?: {
    stores: DashboardStoreFilterOption[];
    value: string | null;
    onChange: (storeId: string | null) => void;
  };
}

const ALL_STORES_VALUE = "";

const DashboardHeader: FC<DashboardHeaderProps> = ({
  onRangeChange,
  metricRange,
  storeFilter
}) => {
  const { t } = useKivunovaTranslation();
  const theme = useTheme();
  const matchMD = useMediaQuery(theme.breakpoints.down("md"));

  const onStoreSelect = (e: SelectChangeEvent<string>) => {
    const v = e.target.value;
    storeFilter?.onChange(v === ALL_STORES_VALUE ? null : v);
  };

  return (
    <Box display="flex" flexDirection="column" mb={2} width="100%">
      <Stack
        direction={matchMD ? "column" : "row"}
        spacing={2}
        width="100%"
        justifyContent="flex-end"
        alignItems={matchMD ? "stretch" : "center"}
      >
        {storeFilter && storeFilter.stores.length > 0 && (
          <Box
            display="flex"
            justifyContent={matchMD ? "center" : "flex-start"}
            width={matchMD ? "100%" : "auto"}
            minWidth={matchMD ? "100%" : 220}
          >
            <FormControl
              size="small"
              fullWidth={matchMD}
              sx={{ minWidth: 200 }}
            >
              <InputLabel id="dashboard-store-label">
                {t("dashboard-store-filter-label", "Store")}
              </InputLabel>
              <Select
                labelId="dashboard-store-label"
                label={t("dashboard-store-filter-label", "Store")}
                value={storeFilter.value ?? ALL_STORES_VALUE}
                onChange={onStoreSelect}
              >
                <MenuItem value={ALL_STORES_VALUE}>
                  {t("dashboard-store-filter-all-stores", "All stores")}
                </MenuItem>
                {storeFilter.stores.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}

        <Box
          display="flex"
          justifyContent={matchMD ? "center" : "flex-start"}
          width={matchMD ? "100%" : "auto"}
        >
          <DateRangeSelector onChange={onRangeChange} value={metricRange} />
        </Box>
      </Stack>
    </Box>
  );
};

export default DashboardHeader;
